import React, { useEffect, useState } from "react";
import { Select } from "../controls";
import { Table as BootstrapTable } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";

interface InputProps {
  setPage: (n: number) => void;
  page: number;
  rowsPerPage: number;
  setRowsPerPage: (n: number) => void;
  columns: any[];
  rows: any[];
  isDone: boolean;
  count: number;
  mobileCard?: (row: any, index: number) => React.ReactNode;
  mobileBreakpoint?: string;
}

const useMatchMedia = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [query]);
  return matches;
};

const Table: React.FC<InputProps> = ({
  columns,
  rows,
  rowsPerPage,
  setRowsPerPage,
  setPage,
  count,
  isDone,
  page,
  mobileCard,
  mobileBreakpoint = "(max-width: 991.98px)",
}) => {
  const emptyRows = rowsPerPage - rows?.length;
  const lastPage = Math.ceil(count / rowsPerPage) - 1;
  const isCompact = useMatchMedia(mobileBreakpoint);
  const useCards = !!mobileCard && isCompact;

  const handleChangePage = (e: any, newPage: number) => {
    e?.preventDefault();
    setPage(newPage);
  };
  const handleFirstPageButtonClick = (e: any) => handleChangePage(e, 0);
  const handleLastPageButtonClick = (e: any) =>
    handleChangePage(e, Math.max(0, lastPage));

  const handleBackButtonClick = (e: any) => {
    if (page === 0) return;
    handleChangePage(e, page - 1);
  };
  const handleNextButtonClick = (e: any) => {
    if (page === lastPage) return;
    handleChangePage(e, page + 1);
  };

  const handleChangeRowsPerPage = (e: any) => {
    const newRowsPerPage = parseInt(e.target.value);
    if (newRowsPerPage === count) {
      setPage(0);
      setRowsPerPage(count);
      return;
    }

    setRowsPerPage(newRowsPerPage);
    const newPage = Math.min(page, Math.floor(rows?.length / newRowsPerPage));
    setPage(newPage);
  };

  const renderPagination = () => (
    <div className="paginationMain">
      <div className="paginationMain__info">
        <div className="paginationMain__pageSize">
          <Select
            showBorder={true}
            options={[
              { value: 10, label: 10 },
              { value: 20, label: 20 },
              { value: 30, label: 30 },
              { value: 50, label: 50 },
              { value: count, label: "All" },
            ]}
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          />
        </div>
        <span className="paginationMain__summary">
          Showing {page * rowsPerPage + 1} to{" "}
          {page === lastPage ? count : page * rowsPerPage + rowsPerPage} of{" "}
          {count} entries
        </span>
      </div>
      <div className="pagination-container">
        <div
          className="pagination-item"
          onClick={handleFirstPageButtonClick}
        >
          <i className="bi bi-chevron-double-left"></i>
        </div>
        <div className="pagination-item" onClick={handleBackButtonClick}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="paginationMain__pageIndicator">
          {page + 1} of {lastPage + 1}
        </div>
        <div className="pagination-item" onClick={handleNextButtonClick}>
          <i className="bi bi-chevron-right"></i>
        </div>
        <div
          className="pagination-item"
          onClick={handleLastPageButtonClick}
        >
          <i className="bi bi-chevron-double-right"></i>
        </div>
      </div>
    </div>
  );

  if (useCards) {
    return (
      <div className="customGrid">
        {!isDone ? (
          <div className="tableCardList">
            {Array.from({ length: Math.min(rowsPerPage || 5, 5) }).map(
              (_, i) => (
                <div className="tableCardList__skeleton" key={`sk-${i}`}>
                  <Skeleton height={20} width="60%" />
                  <div style={{ marginTop: 10 }}>
                    <Skeleton height={14} count={3} />
                  </div>
                </div>
              )
            )}
          </div>
        ) : rows?.length === 0 ? (
          <div className="tableCardList__empty">No Data Found.</div>
        ) : (
          <>
            <div className="tableCardList">
              {rows?.map((row: any, idx: number) => (
                <React.Fragment key={idx}>
                  {mobileCard!(row, idx)}
                </React.Fragment>
              ))}
            </div>
            {renderPagination()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="customGrid">
      {!isDone ? (
        <>
          <BootstrapTable responsive="xl" className="mt-3">
            <thead>
              <tr>
                {columns?.map((elem: any, idx: number) => (
                  <th key={idx} style={{ textAlign: elem?.thAlign }}>
                    {elem.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowsPerPage || 5 }).map((_, r) => (
                <tr key={`sk-${r}`}>
                  {columns?.map((_c: any, c: number) => (
                    <td key={`sk-${r}-${c}`}>
                      <Skeleton height={18} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </BootstrapTable>
          {/* Mirror the real pagination footer so the loading view matches
              the final table height and the layout doesn't jump on data. */}
          <div className="paginationMain">
            <div className="paginationMain__info">
              <Skeleton width={80} height={34} />
              <Skeleton width={160} height={16} />
            </div>
            <div className="pagination-container">
              <Skeleton width={34} height={34} />
              <Skeleton width={34} height={34} />
              <Skeleton width={64} height={28} />
              <Skeleton width={34} height={34} />
              <Skeleton width={34} height={34} />
            </div>
          </div>
        </>
      ) : isDone && rows?.length === 0 ? (
        <BootstrapTable responsive="xl" className="mt-3">
          <thead>
            <tr>
              {columns?.map((elem: any, idx: number) => (
                <th key={idx} style={{ textAlign: elem?.thAlign }}>
                  {elem.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ height: 42 * emptyRows }}>
              <td
                style={{
                  textAlign: "center",
                  alignContent: "center",
                  justifyContent: "center",
                }}
                colSpan={columns?.length}
              >
                No Data Found.
              </td>
            </tr>
          </tbody>
        </BootstrapTable>
      ) : (
        <>
          <BootstrapTable responsive="xl" className="mt-3">
            <thead>
              <tr>
                {columns?.map((elem: any, idx: number) => (
                  <th key={idx} style={{ textAlign: elem?.thAlign }}>
                    {elem.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows?.map((row: any, idxRow: number) => (
                <tr key={idxRow}>
                  {columns?.map((elem: any, idxCol: number) => (
                    <td
                      key={`${idxRow}-${idxCol}`}
                      style={{ textAlign: elem?.tdAlign }}
                    >
                      {elem?.formatter(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {emptyRows > 0 && (
                <tr style={{ height: 42 * emptyRows }}>
                  <td colSpan={columns?.length} />
                </tr>
              )}
            </tbody>
          </BootstrapTable>

          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default Table;
